import dayjs, { Dayjs } from 'dayjs';
import {
  DATE_WITH_TIME,
  DAY_IN_MILLISECONDS,
  DEFAULT_DATE_FORMAT,
  HOUR_IN_MILLISECONDS,
  MINUTE_IN_MILLISECONDS,
  SECOND_IN_MILLISECONDS,
} from './time.content';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  relativeTime: {
    past: '%s ago',
    future: 'in %s',
    s: 'a few seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

export const getCountDown = (targetDate: string, systemTime?: number) => {
  const end = +new Date(targetDate);
  const now = systemTime || Date.now();
  const distance = end - now;

  return {
    days: Math.floor(distance / DAY_IN_MILLISECONDS),
    hours: Math.floor((distance % DAY_IN_MILLISECONDS) / HOUR_IN_MILLISECONDS),
    minutes: Math.floor(
      (distance % HOUR_IN_MILLISECONDS) / MINUTE_IN_MILLISECONDS,
    ),
    seconds: Math.floor(
      (distance % MINUTE_IN_MILLISECONDS) / SECOND_IN_MILLISECONDS,
    ),
  };
};

export const printCountdownDate = (
  countdown: ReturnType<typeof getCountDown>,
  truncate?: boolean,
): string => {
  const { days, hours, minutes } = countdown;

  if (isNaN(days) && isNaN(hours) && isNaN(minutes)) {
    return '';
  }

  const d = `${days > 0 ? days + 'd ' : ''}`;
  const h = `${hours > 0 ? hours + 'h ' : ''}`;
  const m = `${minutes + 'm'}`;

  if (truncate) {
    if (d) {
      return d;
    }

    if (h) {
      return h;
    }

    return minutes > 0 ? m : '<1m';
  }

  return `${d}${h}${m}`;
};

export const calculateTimeDifference = (time: string) => {
  const backendDate = new Date(time);

  const now = new Date();

  const timeDifference = now.getTime() - backendDate.getTime();

  const diffInSeconds = Math.floor(timeDifference / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  return {
    seconds: diffInSeconds % 60,
    minutes: diffInMinutes % 60,
    hours: diffInHours,
    days: diffInDays,
  };
};

export const formatDate = (
  date: Date | string | Dayjs | null,
  format: string = DEFAULT_DATE_FORMAT,
) => dayjs(date).format(format);

export const formatDateWithTime = (
  date: Date | string | Dayjs | null,
  format: string = DATE_WITH_TIME,
) => dayjs(date).format(format);

export const getFromNowDateText = (date: Date | string | Dayjs | null) =>
  dayjs(date).fromNow();

export const isBeforeNow = (date: Date | string | Dayjs) =>
  dayjs(date).isBefore(dayjs());

export const isAfterNow = (date: Date | string | Dayjs) =>
  dayjs(date).isAfter(dayjs());

export const getDateTimeStamp = (date: Date | string | Dayjs) =>
  dayjs(date).valueOf();
export const getPastIsoTimestamp = (
  amount: number,
  unit: dayjs.ManipulateType,
): string => {
  return dayjs().subtract(amount, unit).toISOString();
};

export const isAfterTimestamp = (
  timestampToCheck: string,
  referenceTimestamp: string,
): boolean => {
  return dayjs(timestampToCheck).isAfter(dayjs(referenceTimestamp));
};

export const isBeforeTimestamp = (
  timestampToCheck: string,
  referenceTimestamp: string,
): boolean => {
  return dayjs(timestampToCheck).isBefore(dayjs(referenceTimestamp));
};

export const findEarliestTimestamp = (timestamps: string[]) => {
  if (!timestamps.length) {
    return;
  }

  return timestamps.reduce((earliest, current) => {
    return dayjs(current).isBefore(dayjs(earliest)) ? current : earliest;
  });
};
